/**
 * TasksTab Component
 * TASK-397: Post-Meeting Tabbed Redesign
 *
 * Task board + life events management.
 * Reuses existing PostMeetingHub sub-components.
 */

import React, { useState } from 'react';
import { Task, LifeEvent } from '../../../types/meeting';
import MeetingTaskBoard from '../PostMeetingHub/MeetingTaskBoard';
import LifeEventsCard from '../PostMeetingHub/LifeEventsCard';
import LifeEventModal from '../PostMeetingHub/LifeEventModal';
import AddTaskModal from '../../tasks/AddTaskModal';

// ============================================================================
// Types
// ============================================================================

interface TasksTabProps {
  tasks: Task[];
  lifeEvents: LifeEvent[];
  onAddTask: (task: Task) => void;
  onAddLifeEvent: (event: LifeEvent) => void;
  onEditLifeEvent: (event: LifeEvent) => void;
  onDeleteLifeEvent: (id: string) => void;
}

// ============================================================================
// Component
// ============================================================================

const TasksTab: React.FC<TasksTabProps> = ({
  tasks,
  lifeEvents,
  onAddTask,
  onAddLifeEvent,
  onEditLifeEvent,
  onDeleteLifeEvent,
}) => {
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [lifeEventModalOpen, setLifeEventModalOpen] = useState(false);
  const [editingLifeEvent, setEditingLifeEvent] = useState<LifeEvent | null>(null);

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Task Board */}
      <MeetingTaskBoard
        tasks={tasks}
        onAddTask={() => setTaskModalOpen(true)}
        onOpenTaskModal={() => setTaskModalOpen(true)}
      />

      {/* Life Events */}
      <LifeEventsCard
        events={lifeEvents}
        onAddEvent={() => setLifeEventModalOpen(true)}
        onEditEvent={(event) => {
          setEditingLifeEvent(event);
          setLifeEventModalOpen(true);
        }}
        onDeleteEvent={onDeleteLifeEvent}
      />

      {/* Life Event Modal */}
      {lifeEventModalOpen && (
        <LifeEventModal
          isOpen={lifeEventModalOpen}
          event={editingLifeEvent}
          onClose={() => {
            setLifeEventModalOpen(false);
            setEditingLifeEvent(null);
          }}
          onSave={(event) => {
            if (editingLifeEvent) {
              // Preserve id/source/timestamp — the modal only emits the editable fields.
              onEditLifeEvent({ ...editingLifeEvent, ...event });
            } else {
              onAddLifeEvent({
                ...event,
                id: crypto.randomUUID(),
                source: 'manual',
                timestamp: new Date().toISOString(),
              });
            }
            setLifeEventModalOpen(false);
            setEditingLifeEvent(null);
          }}
        />
      )}

      {/* Add Task Modal — appends to the meeting's local task list; persistence
          rides the existing meeting-save flow (see onAddTask in PostMeetingPage). */}
      <AddTaskModal
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onSubmit={async (req) => {
          onAddTask({
            id: crypto.randomUUID(),
            title: req.title,
            description: req.description,
            priority: req.priority ?? 'medium',
            status: 'pending',
            owner: 'advisor',
            dueDate: req.due_date ?? null,
          });
          setTaskModalOpen(false);
        }}
      />
    </div>
  );
};

export default TasksTab;
